import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, IconButton, InputAdornment, List, ListItemButton, Radio, TextField, Typography,
} from "@mui/material";
import { AutoAwesome, Close, Description, Search } from "@mui/icons-material";
import Api from "../../customlib/Api";
import {
    DETECTION_STATUS, SOURCE_TYPE_LABELS, buildInvitationOrderUrl, buildSakuraOrderUrl, formatRupiah,
    getInvitationSourceType, isLocationReady, missingFieldLabel, summarizeResult,
} from "./invitationOrderUtils";

/**
 * DEV-1483 — jendela "Buat Order dari Undangan" (Skenario 3, 4, 13, 14, 16):
 * pratinjau pesan + Deteksi Undangan -> popup "Undangan Terdeteksi" (ringkasan, data belum
 * ditemukan, Pilih SKU) atau "Deteksi gagal" (Coba Lagi / Lanjut Tanpa Data AI) -> Buka Form
 * Create Order Sakura di tab baru dengan 1 SKU pilihan Tele.
 *
 * mode: "preview" (dari menu ⋮), "continue" (Lanjutkan), "retry" (Coba Lagi), "withoutAi".
 */

const PER_PAGE = 10;

// Master provinsi/kota jarang berubah: cukup diambil sekali per sesi halaman.
let geoCache = null;

const errorMessageOf = (error, fallback) => error?.response?.data?.message || fallback;

const messagePreviewSrc = (message) => {
    if (message?.isS3) return `${Api.s3BaseUrl}${Api.s3Bucket}/${message.objectKey}`;
    if (message?.fileName?.startsWith("http")) return message.fileName;
    return Api.getBaseImageUrl() + message?.fileName;
};

const MessagePreview = ({ data }) => {
    const message = data?.message || {};
    const sourceType = getInvitationSourceType(data, false);
    return (
        <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", p: 1.5, bgcolor: "#fafafa" }}>
            <Typography variant="caption" color="text.secondary">
                Pesan customer · {SOURCE_TYPE_LABELS[sourceType] || "-"}
            </Typography>
            {sourceType === "IMAGE" && (
                <Box component="img" src={messagePreviewSrc(message)} alt="" sx={{ display: "block", maxWidth: "100%", maxHeight: 260, mt: 1, borderRadius: "6px" }} />
            )}
            {sourceType === "PDF" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <Description fontSize="small" color="error" />
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{message.fileName?.split("/").pop() || "Dokumen PDF"}</Typography>
                </Box>
            )}
            {(message.text || message.caption) && (
                <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-line", maxHeight: 200, overflowY: "auto", wordBreak: "break-word" }}>
                    {message.type === "TEXT" ? message.text : message.caption}
                </Typography>
            )}
        </Box>
    );
};

const SkuPicker = ({ geo, provinceId, cityId, onLocationChange, selected, onSelect, disabled }) => {
    const [keyword, setKeyword] = useState("");
    const [products, setProducts] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const reqRef = useRef(0);

    const provinces = geo?.provinces || [];
    const cities = (geo?.cities || []).filter((c) => c.provinceId === provinceId);
    const province = provinces.find((p) => p.id === provinceId) || null;
    const city = cities.find((c) => c.id === cityId) || null;
    const locationReady = isLocationReady(provinceId, cityId);

    const search = useCallback((page = 1, kw = keyword) => {
        if (!isLocationReady(provinceId, cityId)) return;
        const reqId = ++reqRef.current;
        setLoading(true);
        setError("");
        axios
            .get(Api.invitationProducts(), {
                ...Api.getRequestHeaderJsonNew(),
                params: { keyword: kw.trim(), province: provinceId, city: cityId || "", page, per_page: PER_PAGE },
            })
            .then((response) => {
                if (reqId !== reqRef.current) return;
                setProducts(response.data?.data?.products || []);
                setMeta(response.data?.data?.meta || null);
            })
            .catch((err) => {
                if (reqId !== reqRef.current) return;
                setProducts([]);
                setMeta(null);
                setError(errorMessageOf(err, "Pencarian produk gagal, coba lagi."));
            })
            .finally(() => {
                if (reqId === reqRef.current) setLoading(false);
            });
    }, [provinceId, cityId, keyword]);

    // Kota berubah -> muat ulang daftar produk kota itu (keyword dipertahankan).
    useEffect(() => {
        setProducts([]);
        setMeta(null);
        if (isLocationReady(provinceId, cityId)) search(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [provinceId, cityId]);

    const page = meta?.page || 1;
    const totalPages = meta?.total_pages || 1;

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Pilih SKU</Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Autocomplete
                    sx={{ flex: "1 1 200px" }}
                    size="small"
                    options={provinces}
                    value={province}
                    disabled={disabled}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(_e, value) => onLocationChange(value ? value.id : null, null)}
                    renderInput={(params) => <TextField {...params} label="Provinsi" required error={!provinceId} />}
                />
                <Autocomplete
                    sx={{ flex: "1 1 200px" }}
                    size="small"
                    options={cities}
                    value={city}
                    disabled={disabled || !provinceId}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(_e, value) => onLocationChange(provinceId, value ? value.id : null)}
                    renderInput={(params) => <TextField {...params} label="Kota" required={!!provinceId && !locationReady} error={!!provinceId && !locationReady} />}
                />
            </Box>
            {!provinceId && (
                <Typography variant="caption" color="error">Kota acara belum ditemukan. Isi Provinsi/Kota dulu sebelum mencari produk.</Typography>
            )}
            {!!provinceId && !locationReady && (
                <Typography variant="caption" color="error">Pilih Kota dulu: produk Sakura berbeda per kota.</Typography>
            )}

            <TextField
                sx={{ mt: 1.5 }}
                fullWidth
                size="small"
                placeholder="Cari nama / kode produk"
                value={keyword}
                disabled={disabled || !locationReady}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") search(1, e.target.value);
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton size="small" disabled={disabled || !locationReady} onClick={() => search(1)}>
                                <Search fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

            <Box sx={{ mt: 1, maxHeight: 260, overflowY: "auto", border: "1px solid #eee", borderRadius: "6px" }}>
                {loading ? (
                    <Box sx={{ p: 2, textAlign: "center" }}><CircularProgress size={22} /></Box>
                ) : products.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        {locationReady ? "Produk tidak ditemukan di kota ini." : provinceId ? "Pilih kota untuk melihat produk." : "Pilih provinsi untuk melihat produk."}
                    </Typography>
                ) : (
                    <List dense disablePadding>
                        {products.map((p) => (
                            <ListItemButton key={p.id} selected={selected?.id === p.id} disabled={disabled} onClick={() => onSelect(p)} sx={{ gap: 1 }}>
                                <Radio size="small" checked={selected?.id === p.id} sx={{ p: 0.5 }} />
                                {p.image_url && (
                                    <Box component="img" src={p.image_url} alt="" sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: "4px", flexShrink: 0 }}
                                        onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                                )}
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{p.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{p.product_code} · {formatRupiah(p.price)}</Typography>
                                </Box>
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </Box>
            {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <Button size="small" disabled={loading || page <= 1} onClick={() => search(page - 1)}>Sebelumnya</Button>
                    <Typography variant="caption">{page} / {totalPages}</Typography>
                    <Button size="small" disabled={loading || page >= totalPages} onClick={() => search(page + 1)}>Berikutnya</Button>
                </Box>
            )}

            <Box sx={{ mt: 1.5, p: 1.25, borderRadius: "6px", bgcolor: selected ? "#e8f5e9" : "#fff8e1" }}>
                {selected ? (
                    <Typography variant="body2">
                        SKU terpilih: <b>{selected.product_code}</b> — {selected.name} · jumlah 1
                    </Typography>
                ) : (
                    <Typography variant="body2">Belum ada SKU dipilih. Pilih satu produk (1 undangan = 1 SKU).</Typography>
                )}
            </Box>
        </Box>
    );
};

const InvitationOrderDialog = ({ open, data, customerPhone, detectionId, mode = "preview", appMode, onClose, onDetectionChange }) => {
    const [step, setStep] = useState("preview");
    const [detection, setDetection] = useState(null);
    const [withoutAi, setWithoutAi] = useState(false);
    const [error, setError] = useState("");
    const [geo, setGeo] = useState(geoCache);
    const [provinceId, setProvinceId] = useState(null);
    const [cityId, setCityId] = useState(null);
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);

    const applyDetection = useCallback((det) => {
        setDetection(det);
        onDetectionChange && onDetectionChange(det);
        if (det?.orderNumber) {
            setStep("orderExists");
            return;
        }
        const item = det?.item;
        setProvinceId(item?.provinceId ?? det?.result?.provinceId ?? null);
        setCityId(item ? item.cityId ?? null : det?.result?.cityId ?? null);
        setSelected(item ? { id: item.productId, name: item.name, product_code: item.sku } : null);
        setStep("result");
    }, [onDetectionChange]);

    const runDetection = useCallback(() => {
        setStep("loading");
        setError("");
        setWithoutAi(false);
        axios
            .post(Api.invitationDetections(), { waMessageId: data._id }, Api.getRequestHeaderJsonNew())
            .then((response) => applyDetection(response.data?.data?.detection))
            .catch((err) => {
                setError(errorMessageOf(err, "Deteksi undangan gagal dijalankan. Silakan coba lagi."));
                setStep("preview");
            });
    }, [data, applyDetection]);

    const loadDetection = useCallback((id, asWithoutAi) => {
        setStep("loading");
        setError("");
        axios
            .get(Api.invitationDetection(id), Api.getRequestHeaderJsonNew())
            .then((response) => {
                setWithoutAi(!!asWithoutAi);
                applyDetection(response.data?.data?.detection);
            })
            .catch((err) => {
                setError(errorMessageOf(err, "Hasil deteksi tidak bisa dimuat."));
                setStep("preview");
            });
    }, [applyDetection]);

    // Mulai ulang setiap kali jendela dibuka.
    useEffect(() => {
        if (!open) return;
        setDetection(null);
        setSelected(null);
        setError("");
        setWithoutAi(false);
        if (mode === "retry") runDetection();
        else if ((mode === "continue" || mode === "withoutAi") && detectionId) loadDetection(detectionId, mode === "withoutAi");
        else setStep("preview");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open || geoCache) return;
        axios
            .get(Api.invitationGeo(), Api.getRequestHeaderJsonNew())
            .then((response) => {
                geoCache = response.data?.data || null;
                setGeo(geoCache);
            })
            .catch(() => setError("Daftar provinsi/kota tidak bisa dimuat."));
    }, [open]);

    const handleLocationChange = (nextProvinceId, nextCityId) => {
        setProvinceId(nextProvinceId);
        setCityId(nextCityId);
        // Produk Sakura berbeda per kota: pilihan lama tidak berlaku lagi.
        setSelected(null);
    };

    const handleOpenForm = () => {
        if (!selected || !isLocationReady(provinceId, cityId) || !detection) return;
        // Tab dibuka langsung saat klik supaya tidak diblokir popup blocker, lalu diarahkan setelah SKU tersimpan.
        const win = window.open("about:blank", "_blank");
        setSaving(true);
        setError("");
        axios
            .put(
                Api.invitationDetectionItem(detection.id),
                { productId: selected.id, name: selected.name, sku: selected.product_code, provinceId, cityId },
                Api.getRequestHeaderJsonNew()
            )
            .then((response) => {
                const saved = response.data?.data?.detection || detection;
                onDetectionChange && onDetectionChange(saved);
                const url = buildInvitationOrderUrl(appMode, saved.customerPhone || customerPhone, saved.id);
                if (win) win.location.href = url;
                else window.open(url, "_blank");
                onClose();
            })
            .catch((err) => {
                if (win) win.close();
                const orderNumber = err?.response?.data?.data?.orderNumber;
                if (orderNumber) {
                    setDetection((prev) => ({ ...prev, orderNumber }));
                    setStep("orderExists");
                    return;
                }
                setError(errorMessageOf(err, "SKU gagal disimpan. Silakan coba lagi."));
            })
            .finally(() => setSaving(false));
    };

    const isFailed = detection?.status === DETECTION_STATUS.FAILED;
    const showSkuPicker = step === "result" && (!isFailed || withoutAi);
    const summary = summarizeResult(detection?.result);
    const title =
        step === "orderExists" ? "Pesan Sudah Menjadi Order"
            : step === "result" && withoutAi ? "Lanjut Tanpa Data AI"
                : step === "result" && isFailed ? "Deteksi Gagal"
                    : step === "result" ? "Undangan Terdeteksi"
                        : "Buat Order dari Undangan";

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth scroll="paper">
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
                <AutoAwesome sx={{ color: "#7b1fa2" }} /> {title}
                <IconButton onClick={onClose} disabled={saving} sx={{ position: "absolute", right: 8, top: 8 }}><Close /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

                {(step === "preview" || step === "loading") && <MessagePreview data={data} />}

                {step === "loading" && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2 }}>
                        <CircularProgress size={22} />
                        <Typography variant="body2">AI sedang membaca undangan…</Typography>
                    </Box>
                )}

                {step === "orderExists" && (
                    <Alert severity="info">
                        Pesan ini sudah menjadi order <b>{detection?.orderNumber}</b>. Satu undangan hanya bisa menjadi satu order.
                    </Alert>
                )}

                {step === "result" && isFailed && !withoutAi && (
                    <>
                        <Alert severity="error">{detection?.failedMessage || "Undangan tidak terbaca. Pastikan gambar jelas atau isi form order manual."}</Alert>
                        <Typography variant="body2" sx={{ mt: 1.5 }}>
                            Coba Lagi untuk menjalankan AI sekali lagi, atau Lanjut Tanpa Data AI: isi Provinsi/Kota dan pilih SKU, lalu form Sakura terbuka dengan SKU dan data customer saja.
                        </Typography>
                    </>
                )}

                {step === "result" && !isFailed && (
                    <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Data yang terbaca</Typography>
                        <Box component="table" sx={{ width: "100%", fontSize: 13, borderCollapse: "collapse", "& td": { py: 0.5, verticalAlign: "top" } }}>
                            <tbody>
                                {summary.map((row) => (
                                    <tr key={row.key}>
                                        <Box component="td" sx={{ color: "text.secondary", width: 130, pr: 1 }}>{row.label}</Box>
                                        <Box component="td" sx={{ whiteSpace: "pre-line", wordBreak: "break-word" }}>{row.value}</Box>
                                    </tr>
                                ))}
                            </tbody>
                        </Box>
                        {detection?.missingFields?.length > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Belum ditemukan (dilengkapi di form Sakura)</Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {detection.missingFields.map((f) => (
                                        <Chip key={f} size="small" color="warning" variant="outlined" label={missingFieldLabel(f)} />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </>
                )}

                {showSkuPicker && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <SkuPicker
                            geo={geo}
                            provinceId={provinceId}
                            cityId={cityId}
                            onLocationChange={handleLocationChange}
                            selected={selected}
                            onSelect={setSelected}
                            disabled={saving}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {step === "preview" && (
                    <Button variant="contained" startIcon={<AutoAwesome />} onClick={runDetection}>Deteksi Undangan</Button>
                )}
                {step === "loading" && <Button variant="contained" disabled>Deteksi Undangan</Button>}
                {step === "orderExists" && (
                    <Button variant="contained" onClick={() => window.open(buildSakuraOrderUrl(appMode, detection?.orderNumber), "_blank")}>Lihat Order</Button>
                )}
                {step === "result" && isFailed && !withoutAi && (
                    <>
                        <Button onClick={() => setWithoutAi(true)}>Lanjut Tanpa Data AI</Button>
                        <Button variant="contained" onClick={runDetection}>Coba Lagi</Button>
                    </>
                )}
                {showSkuPicker && (
                    <Button variant="contained" disabled={!selected || !isLocationReady(provinceId, cityId) || saving} onClick={handleOpenForm}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
                        Buka Form Create Order
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default InvitationOrderDialog;
